import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import {
  IValuableFeatures,
  ReturnLikelihood,
} from "@/types/subscription.types";
import { useCancelPlanContext } from "./cancel-provider";

const valuableFeaturesOptions: {
  label: string;
  value: keyof IValuableFeatures;
}[] = [
  {
    label: "Email Warmup",
    value: "emailWarmup",
  },
  {
    label: "Email Health Tests",
    value: "emailHealthTests",
  },
  {
    label: "Inbox Placement Tests",
    value: "inboxPlacementTests",
  },
  {
    label: "Custom Templates",
    value: "customTemplates",
  },
  {
    label: "Google Postmaster Integration",
    value: "googlePostmasterIntegration",
  },
  {
    label: "Email Template Coach",
    value: "emailTemplateCoach",
  },
];

const ratingOptions = [
  {
    emojiCode: <span>&#128547;</span>,
    value: 1,
    label: "Terrible",
  },
  {
    emojiCode: <span>&#128577;</span>,
    value: 2,
    label: "Bad",
  },
  {
    emojiCode: <span>&#128528;</span>,
    value: 3,
    label: "Good",
  },
  {
    emojiCode: <span>&#128578;</span>,
    value: 4,
    label: "Great",
  },
  {
    emojiCode: <span>&#128516;</span>,
    value: 5,
    label: "Excellent",
  },
];

const valuableFeaturesSchema = z.object({
  emailWarmup: z.boolean(),
  inboxPlacementTests: z.boolean(),
  googlePostmasterIntegration: z.boolean(),
  emailHealthTests: z.boolean(),
  customTemplates: z.boolean(),
  emailTemplateCoach: z.boolean(),
});

const feedbackFormSchema = z
  .object({
    reason: z.string({
      required_error: "Please select a reason for cancellation",
    }),
    priceFeedback: z.string().optional(),
    missingFeatures: z.string().optional(),
    switchedTo: z.string().optional(),
    switchedToFeatures: z.string().optional(),
    expectedResults: z.string().optional(),
    technicalIssues: z.string().optional(),
    supportFeedback: z.string().optional(),
    otherReason: z.string().optional(),
    rating: z.number().min(1).max(5),
    valuableFeatures: valuableFeaturesSchema,
    improvementFeedback: z
      .string()
      .min(1, "Please provide feedback on how we can improve"),
    returnLikelihood: z.nativeEnum(ReturnLikelihood),
  })
  .superRefine((data, ctx) => {
    // Validate price feedback when reason is too-expensive
    if (data.reason === "too-expensive" && !data.priceFeedback) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Please select a price option",
        path: ["priceFeedback"],
      });
    }

    // Validate missing features when reason is missing-features
    if (data.reason === "missing-features" && !data.missingFeatures) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Please describe the features you need",
        path: ["missingFeatures"],
      });
    }

    // Validate switched to fields when reason is switched
    if (data.reason === "switched") {
      if (!data.switchedTo) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Please specify which service you're switching to",
          path: ["switchedTo"],
        });
      }
      if (!data.switchedToFeatures) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Please describe what features the other service offers",
          path: ["switchedToFeatures"],
        });
      }
    }

    // Validate expected results when reason is poor-results
    if (data.reason === "poor-results" && !data.expectedResults) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Please describe the results you were expecting",
        path: ["expectedResults"],
      });
    }

    // Validate technical issues when reason is technical-issues
    if (data.reason === "technical-issues" && !data.technicalIssues) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Please describe the technical issues you experienced",
        path: ["technicalIssues"],
      });
    }

    // Validate support feedback when reason is poor-support
    if (data.reason === "poor-support" && !data.supportFeedback) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Please describe how we could improve our support",
        path: ["supportFeedback"],
      });
    }

    // Validate other reason when reason is other
    if (data.reason === "other" && !data.otherReason) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Please specify your reason for cancellation",
        path: ["otherReason"],
      });
    }
  });

export type FeedbackFormValues = z.infer<typeof feedbackFormSchema>;

const CancelFeedback = () => {
  const { handleStateChange, state } = useCancelPlanContext();

  const form = useForm<FeedbackFormValues>({
    resolver: zodResolver(feedbackFormSchema),
    defaultValues: state?.feedback || {
      reason: "",
      rating: 0,
      valuableFeatures: {
        emailWarmup: false,
        inboxPlacementTests: false,
        googlePostmasterIntegration: false,
        emailHealthTests: false,
        customTemplates: false,
        emailTemplateCoach: false,
      },
      returnLikelihood: ReturnLikelihood.MAYBE,
    },
  });

  const onSubmit = (data: FeedbackFormValues) => {
    handleStateChange({
      step: 4,
      feedback: data,
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Help Us Improve</CardTitle>
        <CardDescription>
          We'd appreciate your feedback on why you're canceling
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="reason"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-semibold">
                    Why are you canceling your subscription?
                  </FormLabel>
                  <FormControl>
                    <RadioGroup
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                      className="space-y-2"
                    >
                      <div className="space-y-4">
                        <div className="space-y-3">
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem
                              value="too-expensive"
                              id="too-expensive"
                            />
                            <FormLabel htmlFor="too-expensive" className="font-normal">
                              Too expensive
                            </FormLabel>
                          </div>
                          {field.value === "too-expensive" && (
                            <div className="ml-6 px-3 py-2 pb-3 border-l-4 border-muted-foreground/20 bg-muted/50 rounded-r-md">
                              <FormField
                                control={form.control}
                                name="priceFeedback"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>
                                      What would be a more reasonable price?
                                    </FormLabel>
                                    <FormControl>
                                      <RadioGroup
                                        onValueChange={field.onChange}
                                        defaultValue={field.value}
                                        className="space-y-2"
                                      >
                                        <div className="flex items-center space-x-2">
                                          <RadioGroupItem
                                            value="25-less"
                                            id="price-1"
                                          />
                                          <FormLabel htmlFor="price-1" className="font-normal">
                                            25% less than current price
                                          </FormLabel>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                          <RadioGroupItem
                                            value="50-less"
                                            id="price-2"
                                          />
                                          <FormLabel htmlFor="price-2" className="font-normal">
                                            50% less than current price
                                          </FormLabel>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                          <RadioGroupItem
                                            value="other"
                                            id="price-3"
                                          />
                                          <FormLabel htmlFor="price-3" className="font-normal">
                                            Other (please specify in comments)
                                          </FormLabel>
                                        </div>
                                      </RadioGroup>
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            </div>
                          )}

                          <div className="flex items-center space-x-2">
                            <RadioGroupItem
                              value="missing-features"
                              id="missing-features"
                            />
                            <FormLabel htmlFor="missing-features" className="font-normal">
                              Missing features I need
                            </FormLabel>
                          </div>
                          {field.value === "missing-features" && (
                            <div className="ml-6 px-2 py-2 border-l-4 border-muted-foreground/20 bg-muted/50 rounded-r-md">
                              <FormField
                                control={form.control}
                                name="missingFeatures"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>
                                      Which features are you missing?
                                    </FormLabel>
                                    <FormControl>
                                      <Textarea
                                        placeholder="Please describe the features you need that we don't currently offer..."
                                        className="min-h-[80px]"
                                        {...field}
                                      />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            </div>
                          )}

                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="switched" id="switched" />
                            <FormLabel htmlFor="switched" className="font-normal">
                              Switched to another service
                            </FormLabel>
                          </div>
                          {field.value === "switched" && (
                            <div className="ml-6 px-2 py-2 border-l-4 border-muted-foreground/20 bg-muted/50 rounded-r-md space-y-2">
                              <FormField
                                control={form.control}
                                name="switchedTo"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>
                                      Which service are you switching to?
                                    </FormLabel>
                                    <FormControl>
                                      <Textarea
                                        placeholder="Please let us know which service you're switching to..."
                                        className="min-h-[80px] mb-3"
                                        {...field}
                                      />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                              <FormField
                                control={form.control}
                                name="switchedToFeatures"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>
                                      What does this service offer that we
                                      don't?
                                    </FormLabel>
                                    <FormControl>
                                      <Textarea
                                        placeholder="Please describe what features or benefits the other service provides..."
                                        className="min-h-[80px]"
                                        {...field}
                                      />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            </div>
                          )}

                          <div className="flex items-center space-x-2">
                            <RadioGroupItem
                              value="poor-results"
                              id="poor-results"
                            />
                            <FormLabel htmlFor="poor-results" className="font-normal">
                              Not seeing the results I expected
                            </FormLabel>
                          </div>
                          {field.value === "poor-results" && (
                            <div className="ml-6 px-2 py-2 border-l-4 border-muted-foreground/20 bg-muted/50 rounded-r-md">
                              <FormField
                                control={form.control}
                                name="expectedResults"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>
                                      What results were you expecting to see?
                                    </FormLabel>
                                    <FormControl>
                                      <Textarea
                                        placeholder="Please describe the results you were hoping to achieve..."
                                        className="min-h-[80px]"
                                        {...field}
                                      />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            </div>
                          )}

                          <div className="flex items-center space-x-2">
                            <RadioGroupItem
                              value="technical-issues"
                              id="technical-issues"
                            />
                            <FormLabel htmlFor="technical-issues" className="font-normal">
                              Technical issues or bugs
                            </FormLabel>
                          </div>
                          {field.value === "technical-issues" && (
                            <div className="ml-6 px-2 py-2 border-l-4 border-muted-foreground/20 bg-muted/50 rounded-r-md">
                              <FormField
                                control={form.control}
                                name="technicalIssues"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>
                                      What technical issues did you experience?
                                    </FormLabel>
                                    <FormControl>
                                      <Textarea
                                        placeholder="Please describe the technical issues or bugs you encountered..."
                                        className="min-h-[80px]"
                                        {...field}
                                      />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            </div>
                          )}

                          <div className="flex items-center space-x-2">
                            <RadioGroupItem
                              value="poor-support"
                              id="poor-support"
                            />
                            <FormLabel htmlFor="poor-support" className="font-normal">
                              Unsatisfactory customer support
                            </FormLabel>
                          </div>
                          {field.value === "poor-support" && (
                            <div className="ml-6 px-2 py-2 border-l-4 border-muted-foreground/20 bg-muted/50 rounded-r-md">
                              <FormField
                                control={form.control}
                                name="supportFeedback"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormLabel>
                                      How could we improve our support?
                                    </FormLabel>
                                    <FormControl>
                                      <Textarea
                                        placeholder="Please describe how we could improve our customer support..."
                                        className="min-h-[80px]"
                                        {...field}
                                      />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            </div>
                          )}

                          <div className="flex items-center space-x-2">
                            <RadioGroupItem value="other" id="other" />
                            <FormLabel htmlFor="other" className="font-normal">
                              Other reason (please specify)
                            </FormLabel>
                          </div>
                          {field.value === "other" && (
                            <div className="ml-6 px-2 py-2 border-l-4 border-muted-foreground/20 bg-muted/50 rounded-r-md">
                              <FormField
                                control={form.control}
                                name="otherReason"
                                render={({ field }) => (
                                  <FormItem>
                                    <FormControl>
                                      <Textarea
                                        placeholder="Please describe your reason for canceling..."
                                        className="min-h-[80px]"
                                        {...field}
                                      />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="rating"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-semibold">
                    How would you rate your overall experience with our service?
                  </FormLabel>
                  <FormControl>
                    <div className="flex flex-wrap gap-5 mb-4">
                      {ratingOptions.map((rating) => (
                        <div
                          key={rating.value}
                          className="flex flex-col items-center justify-center gap-1"
                        >
                          <Button
                            type="button"
                            variant="outline"
                            className={`h-12 w-12 rounded-full text-4xl pb-1 ${
                              field.value === rating.value
                                ? "bg-blue-100 border-blue-500"
                                : ""
                            }`}
                            onClick={() => field.onChange(rating.value)}
                          >
                            {rating.emojiCode}
                          </Button>
                          <p className="text-xs">{rating.label}</p>
                        </div>
                      ))}
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="valuableFeatures"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-semibold">
                    Which features did you find most valuable?
                  </FormLabel>
                  <FormControl>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {valuableFeaturesOptions?.map((feature, index) => (
                        <div
                          key={index}
                          className="flex items-center space-x-2"
                        >
                          {/* @ts-ignore - field.value may be undefined */}
                          <Checkbox
                            id={`feature-${index}`}
                            checked={field.value?.[feature.value] ?? false}
                            onCheckedChange={(checked) => {
                              const newValue = {
                                ...field.value,
                                [feature.value]: checked,
                              };
                              field.onChange(newValue);
                            }}
                          />
                          <FormLabel htmlFor={`feature-${index}`} className="font-normal">
                            {feature.label}
                          </FormLabel>
                        </div>
                      ))}
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="improvementFeedback"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-semibold">What could we have done better?</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Please share any suggestions for how we could improve our service..."
                      className="min-h-[100px]"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="returnLikelihood"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="font-semibold">
                    Would you consider returning in the future?
                  </FormLabel>
                  <FormControl>
                    <RadioGroup
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                      className="space-y-2"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem
                          value={ReturnLikelihood.YES_DEFINITELY}
                          id="return-yes"
                        />
                        <FormLabel htmlFor="return-yes" className="font-normal">
                          Yes, definitely
                        </FormLabel>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem
                          value={ReturnLikelihood.MAYBE}
                          id="return-maybe"
                        />
                        <FormLabel htmlFor="return-maybe" className="font-normal">
                          Maybe, if improvements are made
                        </FormLabel>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem
                          value={ReturnLikelihood.NO}
                          id="return-no"
                        />
                        <FormLabel htmlFor="return-no" className="font-normal">No, unlikely</FormLabel>
                      </div>
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </Form>
      </CardContent>
      <CardFooter className="flex justify-between">
        <Button
          variant="outline"
          onClick={() => handleStateChange({ step: 2 })}
        >
          Back
        </Button>
        <Button
          type="submit"
          //   disabled={!form.formState.isValid}
          onClick={form.handleSubmit(onSubmit)}
        >
          Continue
        </Button>
      </CardFooter>
    </Card>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const formatCancellationReason = (
  feedback: FeedbackFormValues
): { type: string; details: Record<string, string> } => {
  if (feedback.reason === "too-expensive") {
    return {
      type: "too-expensive",
      details: {
        priceFeedback: feedback.priceFeedback!,
      },
    };
  }
  if (feedback.reason === "missing-features") {
    return {
      type: "missing-features",
      details: {
        missingFeatures: feedback.missingFeatures!,
      },
    };
  }

  if (feedback.reason === "switched") {
    return {
      type: "switched",
      details: {
        switchedTo: feedback.switchedTo!,
        switchedToFeatures: feedback.switchedToFeatures!,
      },
    };
  }

  if (feedback.reason === "poor-results") {
    return {
      type: "poor-results",
      details: {
        expectedResults: feedback.expectedResults!,
      },
    };
  }

  if (feedback.reason === "technical-issues") {
    return {
      type: "technical-issues",
      details: {
        technicalIssues: feedback.technicalIssues!,
      },
    };
  }

  if (feedback.reason === "poor-support") {
    return {
      type: "poor-support",
      details: {
        supportFeedback: feedback.supportFeedback!,
      },
    };
  }

  if (feedback.reason === "other") {
    return {
      type: "other",
      details: {
        otherReason: feedback.otherReason!,
      },
    };
  }

  return {
    type: "other",
    details: {
      otherReason: feedback.otherReason!,
    },
  };
};

export default CancelFeedback;
